import { useMemo, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { get } from 'utils/lodash'
import { useLazyGetTenantSettingsQuery } from 'api/tenant-settings/tenantSettingsCrud'
import { useAuthValue } from 'auth/AuthContext'
import { getSelectedBusinessId } from 'data/select-account/selectAccount'

/**
 * Custom hook to check if a specific tenant setting is enabled
 * @param {string} settingKey - The dot-notation path to the setting (e.g., 'stringValues.paymentsBatch')
 * @returns {Object} - Object containing the setting value, loading state, and full tenant settings
 */
export const useTenantSetting = settingKey => {
  const { currentUser } = useAuthValue()
  const businessId = useSelector(getSelectedBusinessId)
  const [getTenantSettings, { isLoading, data: tenantSettings }] = useLazyGetTenantSettingsQuery()
  const [hasInitialized, setHasInitialized] = useState(false)

  const settingValue = useMemo(() => {
    return get(tenantSettings, `stringValues.${settingKey}`)
  }, [tenantSettings, settingKey])

  useEffect(() => {
    if (!hasInitialized && currentUser && businessId) {
      getTenantSettings()
      setHasInitialized(true)
    }
  }, [getTenantSettings, hasInitialized, currentUser, businessId])

  // Consider it loading if we haven't initialized yet OR if the API call is in progress
  const isLoadingTenantSettings = !hasInitialized || isLoading

  return {
    value: settingValue,
    isLoading: isLoadingTenantSettings,
    tenantSettings,
  }
}
